import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { ContasPagarReceberService } from '../../services/dashboard/contas-pagar-receber.service';
import { TituloDTO, TituloResponseDTO } from '../../models/dashboard/titulo.model';
import { TotaisContasDTO } from '../../models/dashboard/totais-contas.model';
import { ValorPorMoedaDTO } from '../../models/dashboard/totais-contas.model';
import { ContaService } from '../../services/conta/conta.service';
import { TipoDespesaService } from '../../services/enums/tipo-despesa.service';
import { EmpresaConfigService } from '../../services/configuracoes/empresa.service';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { PeriodoFiltro } from '../../models/dashboard/periodo-filtro.model';
import { fmtDateTime } from '../../shared/utils/periodo.utils';
import { PaginatorState } from 'primeng/paginator';
import { ContasPagarReceberConfig, TipoTelaConta, SituacaoTitulo } from './contas-pagar-receber.types';
import { TableColumn } from '../../models/shared/table-column.model';
import { moedasCompativeis } from '../../utils/moeda-pareamento.util';

interface CardTotal {
  simbolo: string;
  valor: number;
  label: string;
  moeda: string;
  flagUrl: string;
  corBorda: string;
}

@Component({
  selector: 'app-contas-pagar-receber',
  templateUrl: './contas-pagar-receber.component.html',
  styleUrls: ['./contas-pagar-receber.component.scss'],
})
export class ContasPagarReceberComponent implements OnInit, OnDestroy {

  // --- Configuracao dinamica ---
  config!: ContasPagarReceberConfig;

  // --- Dados unificados (titulos) ---
  titulos: TituloDTO[] = [];
  titulosOriginal: TituloDTO[] = [];

  // --- Totais ---
  totaisContas: TotaisContasDTO | null = null;
  cardsTotais: CardTotal[] = [];
  totalRecords = 0;

  // --- Paginacao ---
  first = 0;
  rows = 10;
  page = 0;

  // --- Ordenacao (backend sort) ---
  sortField = 'vencimento';
  sortOrder = 1; // 1 = asc, -1 = desc

  // --- Filtros ---
  tipoSelecionado: TipoTelaConta = 'ENTRADA';
  situacaoSelecionada: SituacaoTitulo = 'ABERTO';
  contaSelecionada: number | null = null;
  searchNome = '';
  pessoaId: number | null = null;
  filtroMoedaSelecionada: string | null = null;

  // --- Filtro especifico SAIDA ---
  tipoDespesaSelecionada: string | null = null;
  tipoDespesaOptions: { label: string; value: string }[] = [];
  loadingTipos = false;

  // --- Toggle opcoes ---
  tipoOptions: { label: string; value: TipoTelaConta }[] = [];
  situacaoOptions: { label: string; value: SituacaoTitulo }[] = [];

  // --- Periodo (navegacao por mes) ---
  mesAtual: Date = new Date();
  periodoSelecionado!: PeriodoFiltro;
  usandoPeriodoCustomizado = false;

  // --- Conta dropdown ---
  contas: { label: string; value: number; flagUrl?: string; moeda?: string }[] = [];

  // --- Colunas visiveis ---
  readonly STORAGE_KEY = 'contas-pagar-receber-visible-columns';

  columns: TableColumn[] = [
    { key: 'id', labelKey: 'contas-a-pagar.labels.id', visible: true, fixed: true, defaultVisible: true },
    { key: 'emissao', labelKey: 'contas-a-pagar.labels.data_emissao', visible: true, fixed: false, defaultVisible: true },
    { key: 'observacao', labelKey: 'contas-a-pagar.labels.descricao', visible: true, fixed: false, defaultVisible: true },
    { key: 'tipo', labelKey: 'contas-a-pagar.labels.tipo_despesa', visible: true, fixed: false, defaultVisible: true },
    { key: 'pessoa', labelKey: 'contas-a-pagar.labels.pessoa', visible: true, fixed: false, defaultVisible: true },
    { key: 'valor', labelKey: 'contas-a-pagar.labels.valor', visible: true, fixed: false, defaultVisible: true },
  ];

  // --- Moeda padrao da empresa ---
  private moedaPadraoEmpresa: string | null = null;

  // --- Loading ---
  loading = false;
  loadingTotais = false;
  loadingContas = false;

  // --- Debounce ---
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  // --- Triggers de carregamento (switchMap = ultimo-vence, cancela requisicao anterior em voo) ---
  private carregarListagemTrigger$ = new Subject<void>();
  private carregarTotaisTrigger$ = new Subject<void>();

  // --- Traducoes ---
  texts = {
    titulo_receber: '',
    titulo_pagar: '',
    subtitulo_receber: '',
    subtitulo_pagar: '',
  };

  labels = {
    aberto: '',
    pago: '',
    cancelado: '',
    todas_as_contas: '',
    todos_os_tipos: '',
    nenhum_resultado: '',
    total_guarani: '',
    total_real: '',
    total_dolar: '',
    total_peso: '',
    lista_titulos: '',
    lista_despesas: '',
    tipo_receita: '',
    tipo_despesa: '',
  };

  placeholders = {
    buscar_nome: '',
    buscar_descricao: '',
  };

  toggle = {
    contas_a_receber: '',
    contas_a_pagar: '',
  };

  private langChangeSub!: Subscription;

  constructor(
    private contasPagarReceberService: ContasPagarReceberService,
    private contaService: ContaService,
    private tipoDespesaService: TipoDespesaService,
    private messageService: MessageService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private empresaConfigService: EmpresaConfigService,
    private dashboardService: DashboardService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    // Debounce da busca
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.onFiltroChange();
    });

    // Streams de carregamento com switchMap: cada novo disparo cancela a
    // requisicao anterior ainda em voo, garantindo que a ULTIMA emissao vence
    // de forma deterministica. Resolve a condicao de corrida da carga inicial
    // (periodo showAll x periodo persistido "Este mes" do app-periodo-filtro).
    this.configurarStreamListagem();
    this.configurarStreamTotais();

    // Carregar moeda padrao da empresa para auto-selecionar conta no dropdown
    this.carregarMoedaPadraoEEmpresa();

    // Carregar tipos de despesa (para SAIDA)
    this.carregarTiposDespesa();

    // Reagir a mudancas de queryParams (incluindo navegacao entre ENTRADA/SAIDA no menu)
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const pid = params['pessoaId'];
      this.pessoaId = pid ? Number(pid) : null;

      // Atualizar tipo (ENTRADA/SAIDA) sempre que o queryParam mudar
      const tipo = params['tipo'] as TipoTelaConta;
      if (tipo === 'ENTRADA' || tipo === 'SAIDA') {
        this.tipoSelecionado = tipo;
      }

      // Pre-selecionar situacao (ABERTO/PAGO/CANCELADO)
      const situacao = params['situacao'] as SituacaoTitulo;
      if (situacao === 'ABERTO' || situacao === 'PAGO' || situacao === 'CANCELADO') {
        this.situacaoSelecionada = situacao;
      }

      // Pre-selecionar conta
      const contaId = params['contaId'];
      if (contaId) {
        this.contaSelecionada = Number(contaId);
      }

      // Pre-selecionar periodo se veio do dashboard
      const dataInicio = params['dataInicio'];
      const dataFim = params['dataFim'];
      if (dataInicio && dataFim) {
        this.periodoSelecionado = {
          tipo: 'custom',
          dataInicio: dataInicio,
          dataFim: dataFim,
          texto: '',
        };
        this.usandoPeriodoCustomizado = true;
      }

      // Periodo padrao "Mostrar todos" (showAll) se nao veio customizado do dashboard
      if (!this.periodoSelecionado) {
        this.aplicarPeriodoPadrao();
      }

      // Montar config baseado no tipo selecionado
      this.atualizarConfig();

      // Carregar dados com o tipo atualizado
      this.carregarDados();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // --- Colunas visiveis ---

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  // --- Traducoes ---

  private loadTranslations(): void {
    this.toggle = {
      contas_a_receber: this.translate.instant('contas-cobrar.toggle.contas_a_receber'),
      contas_a_pagar: this.translate.instant('contas-cobrar.toggle.contas_a_pagar'),
    };

    this.tipoOptions = [
      { label: this.toggle.contas_a_receber, value: 'ENTRADA' },
      { label: this.toggle.contas_a_pagar, value: 'SAIDA' },
    ];

    this.texts = {
      titulo_receber: this.translate.instant('contas-cobrar.texts.titulo_receber'),
      titulo_pagar: this.translate.instant('contas-cobrar.texts.titulo_pagar'),
      subtitulo_receber: this.translate.instant('contas-cobrar.texts.subtitulo_receber'),
      subtitulo_pagar: this.translate.instant('contas-cobrar.texts.subtitulo_pagar'),
    };

    this.labels = {
      aberto: this.translate.instant('contas-cobrar.labels.aberto'),
      pago: this.translate.instant('contas-cobrar.labels.pago'),
      cancelado: this.translate.instant('contas-cobrar.labels.cancelado'),
      todas_as_contas: this.translate.instant('contas-cobrar.labels.todas_as_contas'),
      todos_os_tipos: this.translate.instant('contas-a-pagar.labels.todos_os_tipos'),
      nenhum_resultado: this.translate.instant('contas-pagar-receber.labels.nenhum_resultado'),
      total_guarani: this.translate.instant('contas-pagar-receber.labels.total_guarani'),
      total_real: this.translate.instant('contas-pagar-receber.labels.total_real'),
      total_dolar: this.translate.instant('contas-pagar-receber.labels.total_dolar'),
      total_peso: this.translate.instant('contas-pagar-receber.labels.total_peso'),
      lista_titulos: this.translate.instant('contas-cobrar.labels.nome_pessoa'),
      lista_despesas: this.translate.instant('contas-a-pagar.labels.lista_despesas'),
      tipo_receita: this.translate.instant('contas-a-receber.labels.tipo_receita'),
      tipo_despesa: this.translate.instant('contas-a-pagar.labels.tipo_despesa'),
    };

    this.atualizarSituacaoOptions();

    this.placeholders = {
      buscar_nome: this.translate.instant('contas-pagar-receber.placeholders.buscar_nome'),
      buscar_descricao: this.translate.instant('contas-a-pagar.placeholders.buscar_descricao'),
    };

    // Atualizar config apos traduzir
    this.atualizarConfig();

    // Atualizar cards de totais
    this.atualizarCardsTotais();
  }

  private atualizarSituacaoOptions(): void {
    const labelPago = this.tipoSelecionado === 'ENTRADA'
      ? (this.translate.instant('contas-cobrar.labels.recebidos') || 'Recebido')
      : this.labels.pago;
    this.situacaoOptions = [
      { label: this.labels.aberto, value: 'ABERTO' },
      { label: labelPago, value: 'PAGO' },
      { label: this.labels.cancelado, value: 'CANCELADO' },
    ];
  }

  // --- Config dinamica ---

  private atualizarConfig(): void {
    if (this.tipoSelecionado === 'ENTRADA') {
      this.config = {
        tipo: 'ENTRADA',
        titulo: this.texts.titulo_receber,
        subtitulo: this.texts.subtitulo_receber,
        icone: 'pi pi-dollar',
        corPrincipal: '#22c55e',
        corIconeBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        corIconeShadow: 'rgba(34, 197, 94, 0.3)',
        labelAberto: this.labels.aberto,
        labelPago: this.labels.pago,
        tableIcon: 'pi pi-list',
        tableIconBg: 'rgba(34, 197, 94, 0.1)',
        tableIconColor: '#22c55e',
      };
    } else {
      this.config = {
        tipo: 'SAIDA',
        titulo: this.texts.titulo_pagar,
        subtitulo: this.texts.subtitulo_pagar,
        icone: 'pi pi-wallet',
        corPrincipal: '#ef4444',
        corIconeBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        corIconeShadow: 'rgba(239, 68, 68, 0.3)',
        labelAberto: this.labels.aberto,
        labelPago: this.labels.pago,
        tableIcon: 'pi pi-list',
        tableIconBg: 'rgba(239, 68, 68, 0.1)',
        tableIconColor: '#ef4444',
      };
    }
  }

  // --- Periodo / Navegacao por mes ---

  get mesAnoLabel(): string {
    const locale = this.translate.currentLang === 'es' ? 'es-PY' : 'pt-BR';
    const label = this.mesAtual.toLocaleDateString(locale, { month: 'long', year: 'numeric' });
    return label.charAt(0).toUpperCase() + label.slice(1);
  }

  mesAnterior(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() - 1, 1);
    this.usandoPeriodoCustomizado = false;
    this.recalcularPeriodo();
    this.carregarDados();
  }

  proximoMes(): void {
    this.mesAtual = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() + 1, 1);
    this.usandoPeriodoCustomizado = false;
    this.recalcularPeriodo();
    this.carregarDados();
  }

  recalcularPeriodo(): void {
    const inicio = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth(), 1, 0, 0);
    const fim = new Date(this.mesAtual.getFullYear(), this.mesAtual.getMonth() + 1, 0, 23, 59);
    this.periodoSelecionado = {
      tipo: 'thisMonth',
      dataInicio: fmtDateTime(inicio),
      dataFim: fmtDateTime(fim),
      texto: this.mesAnoLabel,
    };
  }

  /**
   * Define o periodo INICIAL da tela como "Mostrar todos" (showAll), alinhado
   * ao app mobile e ao valorPadrao do app-periodo-filtro. Intervalo fixo
   * 01/01/2000 00:00 -> 31/12/3000 23:59. A navegacao por mes (mesAnterior/
   * proximoMes) continua usando recalcularPeriodo().
   */
  aplicarPeriodoPadrao(): void {
    const inicio = new Date(2000, 0, 1, 0, 0);
    const fim = new Date(3000, 11, 31, 23, 59);
    this.periodoSelecionado = {
      tipo: 'showAll',
      dataInicio: fmtDateTime(inicio),
      dataFim: fmtDateTime(fim),
      texto: this.translate.instant('dashboard.filtros.mostrar_todos'),
    };
  }

  onPeriodoChange(periodo: PeriodoFiltro): void {
    this.periodoSelecionado = periodo;
    this.usandoPeriodoCustomizado = true;
    this.onFiltroChange();
  }

  // --- Toggle tipo ---

  onTipoChange(): void {
    this.first = 0;
    this.page = 0;
    this.sortField = 'vencimento';
    this.sortOrder = 1;
    this.filtroMoedaSelecionada = null;
    this.situacaoSelecionada = 'ABERTO';
    this.atualizarSituacaoOptions();
    this.atualizarConfig();
    this.atualizarQueryParams();
    this.carregarDados();
  }

  onSituacaoChange(): void {
    this.atualizarCardsTotais();
    this.onFiltroChange();
  }

  // --- Atualizar queryParams na URL ---

  private atualizarQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tipo: this.tipoSelecionado },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  // --- Moeda padrao da empresa ---

  private carregarMoedaPadraoEEmpresa(): void {
    this.empresaConfigService.getSessao().subscribe({
      next: (sessao) => {
        const empresaId = sessao?.id || sessao?.empresaId || 0;
        if (empresaId) {
          this.dashboardService.getConfiguracaoEmpresa(empresaId).subscribe({
            next: (config) => {
              const moedaEnum = config?.moeda?.enum;
              this.moedaPadraoEmpresa = moedaEnum
                ? String(moedaEnum).toUpperCase()
                : null;
              // Reaplica filtro de moedas permitidas nos cards de totais
              // caso eles tenham sido populados antes da moeda padrao chegar.
              this.atualizarCardsTotais();
              this.carregarContas();
            },
            error: () => {
              this.carregarContas();
            },
          });
        } else {
          this.carregarContas();
        }
      },
      error: () => {
        this.carregarContas();
      },
    });
  }

  // --- Contas dropdown ---

  carregarContas(): void {
    this.loadingContas = true;
    this.contaService.get('/conta').subscribe({
      next: (response: any) => {
        // Filtra contas pelas moedas permitidas da moeda padrao da empresa.
        const permitidas = this.moedasPermitidas;
        this.contas = (response.content || [])
          .filter((c: any) => {
            const moeda = (typeof c.moeda === 'object' && c.moeda?.enum ? c.moeda.enum : c.moeda || '').toString().toUpperCase();
            return permitidas.has(moeda);
          })
          .map((c: any) => ({
            label: c.numeroConta == 0 ? `${c.tituloDaConta} - EFETIVO` : c.tituloDaConta,
            value: c.id,
            flagUrl: this.getFlagUrlByMoeda(c.moeda),
            moeda: (typeof c.moeda === 'object' && c.moeda?.enum ? c.moeda.enum : c.moeda || '').toString().toUpperCase(),
          }));

        // Auto-selecionar a primeira conta da moeda padrao da empresa,
        // mas apenas se nao veio contaId pela URL (queryParam).
        if (!this.contaSelecionada && this.moedaPadraoEmpresa) {
          const contaPadrao = this.contas.find(c => c.moeda === this.moedaPadraoEmpresa);
          if (contaPadrao) {
            this.contaSelecionada = contaPadrao.value;
          }
        }

        this.loadingContas = false;
      },
      error: () => {
        this.loadingContas = false;
      },
    });
  }

  // --- Tipos de despesa (para SAIDA) ---

  carregarTiposDespesa(): void {
    this.loadingTipos = true;
    this.tipoDespesaService.get('/enums/tipoDespesa').subscribe({
      next: (response: any) => {
        const tipos = Array.isArray(response) ? response : (response.content || []);
        this.tipoDespesaOptions = tipos.map((t: any) => ({
          label: t.nome,
          value: t.enum || t.nome,
        }));
        this.loadingTipos = false;
      },
      error: () => {
        this.loadingTipos = false;
      },
    });
  }

  getFlagUrlByMoeda(moeda: any): string {
    const moedaEnum = (typeof moeda === 'object' && moeda?.enum ? moeda.enum : moeda || '').toString().toUpperCase();
    switch (moedaEnum) {
      case 'GUARANI': return 'assets/images/paraguay-flag.jpg';
      case 'REAL': return 'assets/images/br-flag.svg';
      case 'DOLAR': return 'assets/images/usa-flag.svg';
      case 'PESO': return 'assets/images/argentina-flag.svg';
      default: return '';
    }
  }

  getContaFlagUrl(contaId: number): string {
    const conta = this.contas.find(c => c.value === contaId);
    return conta?.flagUrl || '';
  }

  getContaLabel(contaId: number): string {
    const conta = this.contas.find(c => c.value === contaId);
    return conta?.label || '';
  }

  /**
   * Conjunto de moedas permitidas para esta empresa, baseado na moeda padrao
   * configurada e na matriz de pareamento (MOEDAS_COMPATIVEIS).
   * Quando a moeda padrao da empresa nao esta definida, libera as 4 moedas.
   */
  get moedasPermitidas(): Set<string> {
    if (!this.moedaPadraoEmpresa) {
      return new Set(['GUARANI', 'DOLAR', 'REAL', 'PESO']);
    }
    return new Set(moedasCompativeis(this.moedaPadraoEmpresa as any));
  }

  // --- Sort string para backend ---

  private buildSortParam(): string {
    const direction = this.sortOrder === 1 ? 'asc' : 'desc';
    return `${this.sortField},${direction}`;
  }

  // --- Carregar dados ---

  carregarDados(): void {
    this.carregarListagem();
    this.carregarTotais();
  }

  // Dispara a busca da listagem unificada via GET /titulos/buscar.
  // A requisicao real corre no stream configurado em configurarStreamListagem(),
  // onde o switchMap cancela qualquer busca anterior ainda em voo.
  private carregarListagem(): void {
    this.carregarListagemTrigger$.next();
  }

  // Dispara a busca dos totais via GET /titulos/totalContasAReceberAPagarERecebidas.
  private carregarTotais(): void {
    this.carregarTotaisTrigger$.next();
  }

  // Stream da listagem: switchMap garante que apenas a ultima requisicao vence,
  // cancelando a resposta de disparos anteriores (evita a corrida showAll x mes).
  private configurarStreamListagem(): void {
    this.carregarListagemTrigger$.pipe(
      switchMap(() => {
        this.loading = true;
        const usandoFiltroMoeda = !!this.filtroMoedaSelecionada;
        return this.contasPagarReceberService.buscarTitulos({
          page: usandoFiltroMoeda ? 0 : this.page,
          size: usandoFiltroMoeda ? 9999 : this.rows,
          tipoOperacaoFinanceira: this.tipoSelecionado,
          dataHoraInicial: this.periodoSelecionado.dataInicio,
          dataHoraFinal: this.periodoSelecionado.dataFim,
          situacao: this.situacaoSelecionada,
          nomePessoa: this.searchNome || undefined,
          conta: this.contaSelecionada || undefined,
          pessoaId: this.pessoaId || undefined,
          observacao: this.searchNome || undefined,
          sort: this.buildSortParam(),
        }).pipe(
          map((response: TituloResponseDTO) => ({ response, usandoFiltroMoeda })),
          // catchError DENTRO do switchMap para nao encerrar o stream externo:
          // um erro de uma requisicao nao impede as proximas.
          catchError(() => {
            this.loading = false;
            this.messageService.add({
              severity: 'error',
              summary: this.translate.instant('common.erro_servidor'),
            });
            return EMPTY;
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe(({ response, usandoFiltroMoeda }) => {
      this.titulosOriginal = response.content;
      if (usandoFiltroMoeda) {
        this.aplicarFiltroMoedaLocal();
      } else {
        this.titulos = response.content;
        this.totalRecords = response.totalElements;
      }
      this.loading = false;
    });
  }

  // Stream dos totais: mesmo padrao switchMap (ultimo-vence) da listagem.
  private configurarStreamTotais(): void {
    this.carregarTotaisTrigger$.pipe(
      switchMap(() => {
        this.loadingTotais = true;
        return this.contasPagarReceberService.buscarTotais({
          dataHoraInicial: this.periodoSelecionado.dataInicio,
          dataHoraFinal: this.periodoSelecionado.dataFim,
          tipoOperacaoFinanceira: this.tipoSelecionado,
          situacao: this.situacaoSelecionada,
          nomePessoa: this.searchNome || undefined,
          conta: this.contaSelecionada || undefined,
          pessoaId: this.pessoaId || undefined,
          observacao: this.searchNome || undefined,
        }).pipe(
          catchError(() => {
            this.loadingTotais = false;
            return EMPTY;
          }),
        );
      }),
      takeUntil(this.destroy$),
    ).subscribe((totais) => {
      this.totaisContas = totais;
      this.atualizarCardsTotais();
      this.loadingTotais = false;
    });
  }

  // --- Cards de totais ---

  private atualizarCardsTotais(): void {
    if (this.tipoSelecionado === 'ENTRADA') {
      this.atualizarCardsTotaisEntrada();
    } else {
      this.atualizarCardsTotaisSaida();
    }
  }

  get cardsTotaisFiltrados(): any[] {
    if (!this.contaSelecionada) return this.cardsTotais;
    const conta = this.contas.find(c => c.value === this.contaSelecionada);
    if (!conta?.moeda) return this.cardsTotais;
    const filtrado = this.cardsTotais.filter(c => c.moeda === conta.moeda);
    return filtrado.length > 0 ? filtrado : this.cardsTotais;
  }

  private obterTotaisPorTipoESituacao(): ValorPorMoedaDTO | null {
    if (!this.totaisContas) return null;

    if (this.tipoSelecionado === 'ENTRADA') {
      if (this.situacaoSelecionada === 'PAGO') {
        return this.totaisContas.totalContaAReceberRecebidas;
      }
      if (this.situacaoSelecionada === 'CANCELADO') {
        return this.totaisContas.totalContaAReceberCanceladas || null;
      }
      return this.totaisContas.totalContaAReceberAbertas;
    } else {
      if (this.situacaoSelecionada === 'PAGO') {
        return this.totaisContas.totalContaAPagarPagas;
      }
      if (this.situacaoSelecionada === 'CANCELADO') {
        return this.totaisContas.totalContaAPagarCanceladas || null;
      }
      return this.totaisContas.totalContaAPagarAbertas;
    }
  }

  private atualizarCardsTotaisEntrada(): void {
    const totais = this.obterTotaisPorTipoESituacao();
    if (!totais) return;

    const permitidas = this.moedasPermitidas;
    this.cardsTotais = [
      {
        simbolo: 'Gs',
        valor: totais.valorTotalEmGuarani,
        label: this.labels.total_guarani,
        moeda: 'GUARANI',
        flagUrl: 'assets/images/paraguay-flag.jpg',
        corBorda: '#dc2626',
      },
      {
        simbolo: '$',
        valor: totais.valorTotalEmDolar,
        label: this.labels.total_dolar,
        moeda: 'DOLAR',
        flagUrl: 'assets/images/usa-flag.svg',
        corBorda: '#16a34a',
      },
      {
        simbolo: 'R$',
        valor: totais.valorTotalEmReal,
        label: this.labels.total_real,
        moeda: 'REAL',
        flagUrl: 'assets/images/br-flag.svg',
        corBorda: '#2563eb',
      },
      {
        simbolo: '$',
        valor: totais?.valorTotalEmPeso || 0,
        label: this.labels.total_peso,
        moeda: 'PESO',
        flagUrl: 'assets/images/argentina-flag.svg',
        corBorda: '#0ea5e9',
      },
    ].filter(c => permitidas.has(c.moeda));
  }

  private atualizarCardsTotaisSaida(): void {
    const totais = this.obterTotaisPorTipoESituacao();
    if (!totais) return;

    const permitidas = this.moedasPermitidas;
    this.cardsTotais = [
      {
        simbolo: 'Gs',
        valor: totais.valorTotalEmGuarani,
        label: this.labels.total_guarani,
        moeda: 'GUARANI',
        flagUrl: 'assets/images/paraguay-flag.jpg',
        corBorda: '#dc2626',
      },
      {
        simbolo: '$',
        valor: totais.valorTotalEmDolar,
        label: this.labels.total_dolar,
        moeda: 'DOLAR',
        flagUrl: 'assets/images/usa-flag.svg',
        corBorda: '#16a34a',
      },
      {
        simbolo: 'R$',
        valor: totais.valorTotalEmReal,
        label: this.labels.total_real,
        moeda: 'REAL',
        flagUrl: 'assets/images/br-flag.svg',
        corBorda: '#2563eb',
      },
      {
        simbolo: '$',
        valor: totais?.valorTotalEmPeso || 0,
        label: this.labels.total_peso,
        moeda: 'PESO',
        flagUrl: 'assets/images/argentina-flag.svg',
        corBorda: '#0ea5e9',
      },
    ].filter(c => permitidas.has(c.moeda));
  }

  // --- Filtro local por moeda (SAIDA) ---

  onMoedaClicada(moeda: string): void {
    if (this.filtroMoedaSelecionada === moeda) {
      this.filtroMoedaSelecionada = null;
    } else {
      this.filtroMoedaSelecionada = moeda;
    }

    this.first = 0;
    this.page = 0;

    this.aplicarFiltroMoedaLocal();
  }

  private aplicarFiltroMoedaLocal(): void {
    if (!this.filtroMoedaSelecionada) {
      this.titulos = [...this.titulosOriginal];
      this.totalRecords = this.titulosOriginal.length;
      return;
    }

    const moedaFiltro = this.filtroMoedaSelecionada.toUpperCase();
    const filtrados = this.titulosOriginal.filter(d => {
      const moedaTitulo = (typeof d.moeda === 'object' && (d.moeda as any)?.enum
        ? (d.moeda as any).enum
        : d.moeda || '').toString().toUpperCase();
      return moedaTitulo === moedaFiltro;
    });
    this.titulos = filtrados;
    this.totalRecords = filtrados.length;
  }

  // --- Eventos de filtros ---

  onFiltroChange(): void {
    this.first = 0;
    this.page = 0;
    this.carregarDados();
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchNome = value;
    this.searchSubject.next(value);
  }

  // --- Ordenacao ---

  onSortChanged(event: { field: string; order: number }): void {
    this.sortField = event.field;
    this.sortOrder = event.order;
    this.first = 0;
    this.page = 0;
    this.carregarDados();
  }

  // --- Paginacao ---

  onPageChange(event: PaginatorState): void {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 10;
    this.page = Math.floor(this.first / this.rows);
    this.carregarListagem();
  }

  // --- Label dinamico da tabela ---

  get tituloCard(): string {
    return this.tipoSelecionado === 'ENTRADA' ? this.labels.lista_titulos : this.labels.lista_despesas;
  }

  get searchPlaceholder(): string {
    return this.tipoSelecionado === 'ENTRADA' ? this.placeholders.buscar_nome : this.placeholders.buscar_descricao;
  }

  // --- Navegacao para titulos da pessoa ---

  onTituloClicado(titulo: TituloDTO): void {
    const pid = titulo.pessoaId || titulo.pessoa_id || this.pessoaId;
    if (!pid) return;
    const rota = this.tipoSelecionado === 'SAIDA'
      ? ['/contas-a-pagar/pessoa', pid]
      : ['/contas-a-receber/pessoa', pid];

    // Propaga o recorte atual dos filtros para a tela de detalhe, casando
    // exatamente as chaves lidas por titulos-pessoa.component (dataInicio,
    // dataFim, situacao, conta). Cada chave so entra na URL quando ha valor.
    const queryParams: { [key: string]: string | number } = {
      nome: titulo.nomePessoa || '',
      tipo: this.tipoSelecionado,
    };

    if (this.periodoSelecionado?.dataInicio) {
      queryParams['dataInicio'] = this.periodoSelecionado.dataInicio;
    }
    if (this.periodoSelecionado?.dataFim) {
      queryParams['dataFim'] = this.periodoSelecionado.dataFim;
    }
    // Propaga o PRESET de periodo (ex.: 'thisMonth', 'showAll') para o detalhe
    // semear o controle `app-periodo-filtro` e o chip, mantendo controle+chip+
    // dados em sincronia. So enviamos presets nomeados (string, exceto 'custom');
    // intervalos arbitrarios (dashboard: tipo 'custom' ou numerico) NAO sao
    // propagados — o controle nao sabe representa-los e cai no fallback 'showAll',
    // preservando o comportamento atual (dados pelas datas herdadas).
    const periodoTipo = this.periodoSelecionado?.tipo;
    if (typeof periodoTipo === 'string' && periodoTipo !== 'custom') {
      queryParams['periodoTipo'] = periodoTipo;
      if (this.periodoSelecionado?.texto) {
        queryParams['periodoTexto'] = this.periodoSelecionado.texto;
      }
    }
    // O detalhe so aceita ABERTO | PAGO; CANCELADO nao e propagado.
    if (this.situacaoSelecionada && this.situacaoSelecionada !== 'CANCELADO') {
      queryParams['situacao'] = this.situacaoSelecionada;
    }
    if (this.contaSelecionada) {
      queryParams['conta'] = this.contaSelecionada;
    }
    // Titulo clicado: a filha destaca sutilmente a linha correspondente.
    if (titulo.id) {
      queryParams['titulo'] = titulo.id;
    }

    this.router.navigate(rota, { queryParams });
  }
}
